/**
 * Montos y números con la moneda y el idioma de la empresa (llegan al ingresar: `currency` y `locale`).
 * Nada de pesos colombianos escritos en el código: cada empresa ve su moneda (COP, BRL, MXN, USD…).
 */
const CURRENCY_KEY = 'ruttia.currency';
const LOCALE_KEY = 'ruttia.locale';

/** Monedas que en el negocio se muestran sin decimales. */
const NO_DECIMALS = new Set(['COP', 'CLP', 'PYG', 'VND', 'KRW', 'JPY', 'IDR', 'UGX', 'XOF', 'XAF']);

const read = (key: string) => {
  try {
    return localStorage.getItem(key);
  } catch {
    return null;
  }
};

const validCurrency = (v: unknown) => typeof v === 'string' && /^[A-Z]{3}$/.test(v);
const validLocale = (v: unknown) => {
  if (typeof v !== 'string' || !v) return false;
  try {
    return Intl.NumberFormat.supportedLocalesOf([v]).length > 0;
  } catch {
    return false;
  }
};

let currentCurrency = validCurrency(read(CURRENCY_KEY)) ? (read(CURRENCY_KEY) as string) : 'COP';
let currentLocale = validLocale(read(LOCALE_KEY)) ? (read(LOCALE_KEY) as string) : 'es-CO';

/** Al ingresar y al pedir el usuario actual el API manda la moneda y el idioma de la empresa. */
export const setCompanyFormat = (data: { currency?: unknown; locale?: unknown }) => {
  if (validCurrency(data.currency)) {
    currentCurrency = data.currency as string;
    try {
      localStorage.setItem(CURRENCY_KEY, currentCurrency);
    } catch {
      /* sin almacenamiento: queda en memoria */
    }
  }
  if (validLocale(data.locale)) {
    currentLocale = data.locale as string;
    try {
      localStorage.setItem(LOCALE_KEY, currentLocale);
    } catch {
      /* sin almacenamiento: queda en memoria */
    }
  }
};

export const companyCurrency = () => currentCurrency;
export const companyLocale = () => currentLocale;
export const currencyDecimals = (currency = currentCurrency) => (NO_DECIMALS.has(currency) ? 0 : 2);

/** «$ 1.250.000» (COP, es-CO), «R$ 1.250,00» (BRL, pt-BR)… */
export const formatCurrency = (value: number | string | null | undefined, locale = currentLocale, currency = currentCurrency) => {
  const n = Number(value ?? 0);
  const digits = currencyDecimals(currency);
  return new Intl.NumberFormat(locale, {
    style: 'currency',
    currency,
    minimumFractionDigits: digits,
    maximumFractionDigits: digits,
  }).format(Number.isFinite(n) ? n : 0);
};

/** Símbolo de la moneda de la empresa («$», «R$», «US$»…). */
export const currencySymbol = () =>
  new Intl.NumberFormat(currentLocale, { style: 'currency', currency: currentCurrency }).formatToParts(0).find((p) => p.type === 'currency')?.value ?? currentCurrency;

/** Número con los separadores de la empresa («1.250» en es-CO, «1,250» en en-US). */
export const formatNumber = (value: number | string | null | undefined, maximumFractionDigits = 0) => {
  const n = Number(value ?? 0);
  return new Intl.NumberFormat(currentLocale, { maximumFractionDigits }).format(Number.isFinite(n) ? n : 0);
};

/** Fecha y hora con el formato del idioma de la empresa. */
export const formatLocaleDateTime = (value: Date) => value.toLocaleString(currentLocale);
export const formatLocaleDate = (value: Date) => value.toLocaleDateString(currentLocale);
